import { createContext, useCallback, useContext, useEffect } from "react";
import { createPath, NavigationType, useBlocker, type BlockerFunction } from "react-router";

import type { VersionWatch } from "./build-version.js";
import { LeaveContext } from "./navigation.js";

/**
 * ビルドの見張り（<c>app/build-version.ts</c>）。<b>無ければ（null）何もしない</b>——
 * 入口（<c>main.tsx</c>）が、今の JS の ID が分かったときだけ渡す。テストは渡したいときだけ渡す。
 */
export const VersionWatchContext = createContext<VersionWatch | null>(null);

/**
 * 配信の後に開いたままの画面を、新しいビルドへ切り替える（docs/08 §4.6）。
 *
 * - <b>読みに行くのは、別の画面へ移るときと、タブに戻ってきたとき</b>（visibilitychange）。間隔は見張りが空ける
 * - <b>ID が違うと分かったら、次に別の画面へ移るときにページごと読み込む</b>（画面の中の移動を止めて、同じ行き先を開く）。
 *   入力中の画面の上では読み直さない——移るときだけ切り替える。クエリだけが変わるとき（検索・絞り込み）も切り替えない
 * - 「戻る」「進む」はブラウザが URL を変えた後に届くので、止めずに今の URL を開き直す
 */
export function BuildVersionWatch(): null {
    const watch = useContext(VersionWatchContext);
    const leave = useContext(LeaveContext);

    useEffect(() => {
        if (watch === null) {
            return;
        }
        const onVisibilityChange = (): void => {
            if (document.visibilityState === "visible") {
                watch.check();
            }
        };
        document.addEventListener("visibilitychange", onVisibilityChange);
        return () => {
            document.removeEventListener("visibilitychange", onVisibilityChange);
        };
    }, [watch]);

    const shouldBlock = useCallback<BlockerFunction>(({ currentLocation, nextLocation, historyAction }) => {
        if (watch === null
            || currentLocation.pathname.toLowerCase() === nextLocation.pathname.toLowerCase()) {
            return false;
        }
        if (!watch.stale()) {
            watch.check();
            return false;
        }
        leave(createPath(nextLocation));
        // 「戻る」「進む」を止めると、React Router が履歴を戻し直し、開き直しとぶつかる
        return historyAction !== NavigationType.Pop;
    }, [watch, leave]);

    useBlocker(shouldBlock);
    return null;
}
